import { Loader2, Search, type LucideIcon } from "lucide-react";
import {
  forwardRef,
  useId,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";

type Variant = "primary" | "secondary" | "ghost" | "subtle" | "danger";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: "sm" | "md" | "lg";
  icon?: LucideIcon;
  iconRight?: LucideIcon;
  loading?: boolean;
  pressed?: boolean;
}

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "secondary", size = "md", icon: Icon, iconRight: IconRight, loading, pressed, className, children, disabled, type, ...rest },
  ref,
) {
  const iconSize = size === "sm" ? 14 : 16;
  return (
    <button
      ref={ref}
      type={type ?? "button"}
      className={cx(
        "ds-btn",
        variant !== "secondary" && `ds-btn--${variant}`,
        size !== "md" && `ds-btn--${size}`,
        !children && "ds-btn--icon",
        className,
      )}
      disabled={disabled || loading}
      aria-pressed={pressed === undefined ? undefined : pressed}
      {...rest}
    >
      {loading ? <Loader2 size={iconSize} className="ds-spin" /> : Icon ? <Icon size={iconSize} /> : null}
      {children}
      {IconRight && !loading ? <IconRight size={iconSize} /> : null}
    </button>
  );
});

export function IconButton({ icon, label, ...rest }: Omit<ButtonProps, "children"> & { icon: LucideIcon; label: string }) {
  return <Button icon={icon} aria-label={label} title={label} variant={rest.variant ?? "ghost"} {...rest} />;
}

export function Field({ label, hint, error, children, htmlFor }: { label?: ReactNode; hint?: ReactNode; error?: ReactNode; children: ReactNode; htmlFor?: string }) {
  return (
    <div className="ds-field">
      {label ? <label htmlFor={htmlFor}>{label}</label> : null}
      {children}
      {error ? <span className="ds-error-text">{error}</span> : hint ? <span className="ds-hint">{hint}</span> : null}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { inputSize?: "sm" | "md" }>(function Input(
  { className, inputSize, ...rest },
  ref,
) {
  return <input ref={ref} className={cx("ds-input", inputSize === "sm" && "ds-input--sm", className)} {...rest} />;
});

export function SearchInput({ value, onChange, placeholder = "Search…", autoFocus, width }: { value: string; onChange: (v: string) => void; placeholder?: string; autoFocus?: boolean; width?: number | string }) {
  return (
    <div className="ds-input-wrap" style={{ width: width ?? 280 }}>
      <Search size={15} />
      <input className="ds-input" value={value} placeholder={placeholder} autoFocus={autoFocus} onChange={(e) => onChange(e.target.value)} aria-label={placeholder} />
    </div>
  );
}

export function Select({ className, inputSize, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & { inputSize?: "sm" | "md" }) {
  return (
    <select className={cx("ds-select", inputSize === "sm" && "ds-select--sm", className)} {...rest}>
      {children}
    </select>
  );
}

export function Textarea({ className, code, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement> & { code?: boolean }) {
  return <textarea className={cx("ds-textarea", code && "ds-textarea--code", className)} {...rest} />;
}

export function Checkbox({ label, checked, onChange, disabled }: { label: ReactNode; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <label className="ds-check">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

export function Switch({ label, checked, onChange, disabled }: { label?: ReactNode; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  const id = useId();
  return (
    <label className="ds-switch" htmlFor={id}>
      <input id={id} type="checkbox" role="switch" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="ds-switch__track" />
      {label}
    </label>
  );
}

export function Segmented<T extends string>({ value, options, onChange, size }: { value: T; options: Array<{ value: T; label: ReactNode; icon?: LucideIcon; title?: string }>; onChange: (v: T) => void; size?: "sm" }) {
  return (
    <div className="ds-seg" role="group" style={size === "sm" ? { padding: 2 } : undefined}>
      {options.map((o) => (
        <button key={o.value} type="button" className="ds-seg__btn" aria-pressed={o.value === value} title={o.title} onClick={() => onChange(o.value)}>
          {o.icon ? <o.icon size={14} /> : null}
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Tabs<T extends string>({ value, tabs, onChange }: { value: T; tabs: Array<{ value: T; label: ReactNode; icon?: LucideIcon; count?: number }>; onChange: (v: T) => void }) {
  return (
    <div className="ds-tabs" role="tablist">
      {tabs.map((t) => (
        <button key={t.value} type="button" role="tab" className="ds-tab" aria-selected={t.value === value} onClick={() => onChange(t.value)}>
          {t.icon ? <t.icon size={15} /> : null}
          {t.label}
          {t.count !== undefined ? <span className="ds-badge" style={{ height: 18 }}>{t.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

export { cx };
